import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from './ui/dialog';
import { format, startOfMonth, startOfYear, subDays } from 'date-fns';
import { Plus, Trash2, TrendingUp, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAD_TYPES = ['IUL', 'FEX', 'General Life', 'Annuity', 'Mortgage Protection'];

const fmtCurrency = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
// Build "yyyy-MM-dd" for today in the user's LOCAL timezone (NOT UTC).
const todayISO = () => format(new Date(), 'yyyy-MM-dd');
// Parse a "yyyy-MM-dd" string as a LOCAL date (not UTC) to avoid the off-by-one
// bug where new Date("2026-02-15") becomes Feb 14 in US timezones.
const parseLocalDate = (iso) => {
  if (!iso) return new Date();
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

/**
 * Returns the color tier for a given ROI %.
 *   < 300%       → red    (loss-making / poor)
 *   300% – 499%  → yellow (acceptable)
 *   500%+        → green  (strong)
 *   null         → neutral grey (spend was 0 — undefined)
 */
function roiTier(roiPercent) {
  if (roiPercent == null) {
    return { label: 'N/A', bg: 'bg-[#F4F7FE]', text: 'text-[#A3AED0]', barBg: 'bg-[#A3AED0]' };
  }
  if (roiPercent < 300) {
    return { label: roiPercent.toFixed(0) + '%', bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]', barBg: 'bg-[#EF4444]' };
  }
  if (roiPercent < 500) {
    return { label: roiPercent.toFixed(0) + '%', bg: 'bg-[#FEF3C7]', text: 'text-[#B45309]', barBg: 'bg-[#F59E0B]' };
  }
  return { label: roiPercent.toFixed(0) + '%', bg: 'bg-[#DCFCE7]', text: 'text-[#15803D]', barBg: 'bg-[#01B574]' };
}

export default function LeadRoiReport() {
  const { getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_spend: 0, total_annual_premium: 0, total_sales: 0, weighted_roi_percent: null, entry_count: 0 });
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Time-window filter for the table + summary tiles
  const [timeWindow, setTimeWindow] = useState('all');

  // Form state — date is stored as "yyyy-MM-dd" so a native date input can drive it
  const blankForm = {
    date: todayISO(),
    lead_source: '',
    lead_type: '',
    spend: '',
    sales: '',
    annual_premium: '',
  };
  const [form, setForm] = useState(blankForm);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tracker/lead-roi`, getAuthHeader());
      setEntries(res.data.entries || []);
      setSummary(res.data.summary || {});
    } catch (e) {
      console.error('Failed to load ROI entries', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Filter entries by selected time window. "All time" returns everything.
  // Summary tiles recompute from the filtered subset so the Overall ROI %
  // always reflects what's visible.
  const filteredEntries = useMemo(() => {
    if (timeWindow === 'all') return entries;
    const now = new Date();
    let cutoff;
    if (timeWindow === 'mtd') cutoff = startOfMonth(now);
    else if (timeWindow === 'ytd') cutoff = startOfYear(now);
    else if (timeWindow === '30d') cutoff = subDays(now, 30);
    else if (timeWindow === '90d') cutoff = subDays(now, 90);
    else return entries;
    const cutoffISO = format(cutoff, 'yyyy-MM-dd');
    return entries.filter(e => (e.date || '') >= cutoffISO);
  }, [entries, timeWindow]);

  const filteredSummary = useMemo(() => {
    if (timeWindow === 'all') return summary;
    const totalSpend = filteredEntries.reduce((s, e) => s + (Number(e.spend) || 0), 0);
    const totalAP = filteredEntries.reduce((s, e) => s + (Number(e.annual_premium) || 0), 0);
    const totalSales = filteredEntries.reduce((s, e) => s + (Number(e.sales) || 0), 0);
    const weightedROI = totalSpend > 0 ? (totalAP / totalSpend) * 100 : null;
    return {
      total_spend: totalSpend,
      total_annual_premium: totalAP,
      total_sales: totalSales,
      weighted_roi_percent: weightedROI,
      entry_count: filteredEntries.length,
    };
  }, [filteredEntries, summary, timeWindow]);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || todayISO(),
      lead_source: entry.lead_source || '',
      lead_type: entry.lead_type || '',
      spend: String(entry.spend ?? ''),
      sales: String(entry.sales ?? ''),
      annual_premium: String(entry.annual_premium ?? ''),
    });
    setDialogOpen(true);
  };

  // Live preview of ROI as user types
  const previewSpend = parseFloat(form.spend) || 0;
  const previewAP = parseFloat(form.annual_premium) || 0;
  const previewROI = previewSpend > 0 ? (previewAP / previewSpend) * 100 : null;
  const previewTier = roiTier(previewROI);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.error('Lead purchase date is required');
      return;
    }
    if (!form.lead_source.trim()) {
      toast.error('Lead source is required');
      return;
    }
    const spendNum = parseFloat(form.spend);
    if (Number.isNaN(spendNum) || spendNum < 0) {
      toast.error('Lead spend must be a non-negative number');
      return;
    }
    const apNum = parseFloat(form.annual_premium) || 0;
    const salesNum = parseInt(form.sales, 10) || 0;

    const payload = {
      date: form.date,
      lead_source: form.lead_source.trim(),
      lead_type: form.lead_type || null,
      spend: spendNum,
      sales: salesNum,
      annual_premium: apNum,
    };

    try {
      setSubmitting(true);
      if (editingId) {
        await axios.put(`${API}/tracker/lead-roi/${editingId}`, payload, getAuthHeader());
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/tracker/lead-roi`, payload, getAuthHeader());
        toast.success('Entry added');
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ROI entry?')) return;
    try {
      await axios.delete(`${API}/tracker/lead-roi/${id}`, getAuthHeader());
      toast.success('Deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const overallTier = roiTier(filteredSummary.weighted_roi_percent);

  const TIME_WINDOWS = [
    { k: 'all', label: 'All Time' },
    { k: 'ytd', label: 'YTD' },
    { k: 'mtd', label: 'This Month' },
    { k: '30d', label: 'Last 30 Days' },
    { k: '90d', label: 'Last 90 Days' },
  ];

  return (
    <Card className="bg-white" data-testid="lead-roi-section">
      <CardContent className="space-y-5 p-5">
        {/* Header + summary */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3AED0]">
              <TrendingUp className="h-3.5 w-3.5" /> Lead ROI Report
            </div>
            <div className="mt-1 font-['Manrope'] text-xl font-extrabold text-[#2B3674]">
              Where your lead dollars are working hardest
            </div>
            <div className="mt-0.5 text-[11px] text-[#A3AED0]">
              ROI = annual premium ÷ lead spend. Red &lt; 300% · Yellow 300–499% · Green 500%+
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                className="gap-1.5 bg-[#2B3674] text-white hover:bg-[#1f284f]"
                data-testid="roi-add-btn"
              >
                <Plus className="h-4 w-4" /> Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit ROI entry' : 'Log a new ROI entry'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label className="text-xs" htmlFor="roi-date-input">Lead Purchase Date</Label>
                  <Input
                    id="roi-date-input"
                    type="date"
                    value={form.date}
                    max={todayISO()}
                    onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                    data-testid="roi-form-date"
                  />
                  <div className="mt-1 text-[10px] text-[#A3AED0]">
                    Pick any past date to log historical ROI.
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Lead Source <span className="text-rose-500">*</span></Label>
                  <Input
                    value={form.lead_source}
                    onChange={(e) => setForm(f => ({ ...f, lead_source: e.target.value }))}
                    placeholder="e.g. Facebook Ads, LeadGenius, Referral, Cold List"
                    data-testid="roi-form-source"
                  />
                </div>
                <div>
                  <Label className="text-xs">Lead Type</Label>
                  <Select
                    value={form.lead_type}
                    onValueChange={(v) => setForm(f => ({ ...f, lead_type: v }))}
                  >
                    <SelectTrigger data-testid="roi-form-type">
                      <SelectValue placeholder="Select lead type" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_TYPES.map(t => (
                        <SelectItem key={t} value={t} data-testid={`roi-form-type-${t}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Lead Spend ($) <span className="text-rose-500">*</span></Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.spend}
                      onChange={(e) => setForm(f => ({ ...f, spend: e.target.value }))}
                      placeholder="0"
                      data-testid="roi-form-spend"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Sales Made</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.sales}
                      onChange={(e) => setForm(f => ({ ...f, sales: e.target.value }))}
                      placeholder="0"
                      data-testid="roi-form-sales"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Annual Premium Generated ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.annual_premium}
                    onChange={(e) => setForm(f => ({ ...f, annual_premium: e.target.value }))}
                    placeholder="0"
                    data-testid="roi-form-ap"
                  />
                </div>

                {/* Live ROI preview */}
                <div className={`flex items-center justify-between rounded-xl p-3 ${previewTier.bg}`}>
                  <div className="text-xs font-semibold text-[#2B3674]">Live ROI preview</div>
                  <div className={`font-['Manrope'] text-2xl font-extrabold ${previewTier.text}`} data-testid="roi-preview">
                    {previewTier.label}
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="bg-[#2B3674] text-white hover:bg-[#1f284f]" data-testid="roi-form-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? 'Save changes' : 'Add entry')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Time-window filter pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3AED0] mr-1">Show:</span>
          {TIME_WINDOWS.map(w => (
            <button
              key={w.k}
              type="button"
              onClick={() => setTimeWindow(w.k)}
              data-testid={`roi-window-${w.k}`}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                timeWindow === w.k
                  ? 'bg-[#2B3674] text-white shadow-sm'
                  : 'bg-[#F4F7FE] text-[#2B3674] hover:bg-[#E9EDF7]'
              }`}
            >
              {w.label}
            </button>
          ))}
          {timeWindow !== 'all' && (
            <span className="ml-1 text-[10px] text-[#A3AED0]">
              {filteredEntries.length} of {entries.length} entries
            </span>
          )}
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-[#EDF1FA] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3AED0]">Total Spend</div>
            <div className="mt-1 font-['Manrope'] text-xl font-extrabold text-[#2B3674]" data-testid="roi-summary-spend">
              {fmtCurrency(filteredSummary.total_spend)}
            </div>
          </div>
          <div className="rounded-2xl border border-[#EDF1FA] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3AED0]">Total AP</div>
            <div className="mt-1 font-['Manrope'] text-xl font-extrabold text-[#2B3674]" data-testid="roi-summary-ap">
              {fmtCurrency(filteredSummary.total_annual_premium)}
            </div>
          </div>
          <div className="rounded-2xl border border-[#EDF1FA] p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3AED0]">Total Sales</div>
            <div className="mt-1 font-['Manrope'] text-xl font-extrabold text-[#2B3674]" data-testid="roi-summary-sales">
              {filteredSummary.total_sales || 0}
            </div>
          </div>
          <div className={`rounded-2xl p-3 ${overallTier.bg}`}>
            <div className={`text-[10px] font-bold uppercase tracking-wider ${overallTier.text}`}>Overall ROI</div>
            <div className={`mt-1 font-['Manrope'] text-xl font-extrabold ${overallTier.text}`} data-testid="roi-summary-overall">
              {overallTier.label}
            </div>
          </div>
        </div>

        {/* Entries list */}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-xs text-[#A3AED0]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#E9EDF7] py-10 text-center">
            <TrendingUp className="h-7 w-7 text-[#D9E1F2]" />
            <div className="text-sm font-semibold text-[#A3AED0]">
              {entries.length === 0
                ? 'No ROI entries yet'
                : `No entries in this window`}
            </div>
            <div className="text-[11px] text-[#A3AED0]">
              {entries.length === 0
                ? 'Click "Add Entry" to track your first lead-source spend.'
                : 'Try switching the filter above or log a new entry.'}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[#EDF1FA]">
            {/* Desktop table */}
            <div className="hidden sm:block">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F7FE] text-[10px] font-bold uppercase tracking-wider text-[#A3AED0]">
                  <tr>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Lead Source</th>
                    <th className="px-4 py-2 text-right">Spend</th>
                    <th className="px-4 py-2 text-right">Sales</th>
                    <th className="px-4 py-2 text-right">Annual Premium</th>
                    <th className="px-4 py-2 text-right">ROI</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, idx) => {
                    const tier = roiTier(entry.roi_percent);
                    return (
                      <motion.tr
                        key={entry.id}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        className="border-t border-[#EDF1FA]"
                        data-testid={`roi-row-${entry.id}`}
                      >
                        <td className="px-4 py-3 text-xs text-[#2B3674] whitespace-nowrap">
                          {format(parseLocalDate(entry.date), 'MMM d, yyyy')}
                        </td>
                        <td className="px-4 py-3 text-[#2B3674] font-medium">
                          <div>{entry.lead_source}</div>
                          {entry.lead_type ? (
                            <div className="mt-0.5 inline-flex items-center rounded-full bg-[#F4F7FE] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2B3674]">
                              {entry.lead_type}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#2B3674]">{fmtCurrency(entry.spend)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#2B3674]">{entry.sales}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#2B3674]">{fmtCurrency(entry.annual_premium)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${tier.bg} ${tier.text}`}>
                            {tier.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(entry)} className="h-7 w-7 p-0" data-testid={`roi-edit-${entry.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600" data-testid={`roi-delete-${entry.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 p-2 sm:hidden">
              {filteredEntries.map((entry) => {
                const tier = roiTier(entry.roi_percent);
                return (
                  <div key={entry.id} className="rounded-xl border border-[#EDF1FA] p-3" data-testid={`roi-card-${entry.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#2B3674] truncate">{entry.lead_source}</div>
                        <div className="flex items-center gap-1.5">
                          <div className="text-[10px] text-[#A3AED0]">{format(parseLocalDate(entry.date), 'MMM d, yyyy')}</div>
                          {entry.lead_type ? (
                            <span className="inline-flex items-center rounded-full bg-[#F4F7FE] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#2B3674]">
                              {entry.lead_type}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${tier.bg} ${tier.text}`}>
                        {tier.label}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <div className="text-[#A3AED0]">Spend</div>
                        <div className="font-semibold text-[#2B3674]">{fmtCurrency(entry.spend)}</div>
                      </div>
                      <div>
                        <div className="text-[#A3AED0]">Sales</div>
                        <div className="font-semibold text-[#2B3674]">{entry.sales}</div>
                      </div>
                      <div>
                        <div className="text-[#A3AED0]">AP</div>
                        <div className="font-semibold text-[#2B3674]">{fmtCurrency(entry.annual_premium)}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(entry)} className="h-7 gap-1 text-xs">
                        <Pencil className="h-3 w-3" /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="h-7 gap-1 text-xs text-red-500 hover:text-red-600">
                        <Trash2 className="h-3 w-3" /> Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
